import {ChangeDetectionStrategy, Component, Inject, Injectable, OnInit, signal} from "@angular/core";
import {ForfalteOppgaverDokumentValgSpesifikasjon, GruppeId, OppgaveForelderType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {firstValueFrom} from "rxjs";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {i18n} from "felles-ng2/tjenester/i18n/Oversettelser";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";

const debug = initDebugger('ForfalteOppgaverDokumentValgDialogKomponent');

@Component({
    selector: 'forfalte-oppgaver-dokument-valg-dialog',
    templateUrl: 'forfalte-oppgaver-dokument-valg-dialog.html',
    styleUrl: 'forfalte-oppgaver-dokument-valg-dialog.scss',
    host: {'data-testid': 'forfalte-oppgaver-dokument-valg-dialog'},
    imports: [
        MatDialogContent,
        MatDialogActions,
        MatButton,
        MatIconButton,
        MatIcon,
        MatToolbar,
        MatSlideToggle,
        TranslateModule,
        ReactiveFormsModule,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForfalteOppgaverDokumentValgDialogKomponent implements OnInit {

    static vis(dialog: MatDialog, gruppeId: GruppeId): MatDialogRef<ForfalteOppgaverDokumentValgDialogKomponent, void> {
        return dialog.open(ForfalteOppgaverDokumentValgDialogKomponent, {
            data: {gruppeId} satisfies DialogParametre,
            panelClass: 'forfalte-oppgaver-dokument-valg-dialog',
            ariaLabelledBy: 'forfalte-oppgaver-dokument-valg-dialog-tittel',
            maxWidth: '600px'
        });
    }

    deler = signal<DelModell[]>([]);
    lagrer = asynkArbeidPågårSignal();

    get ingenDelerValgt(): boolean {
        return this.deler().length > 0 && this.deler().every(d => !d.kontroll.value);
    }

    constructor(
        @Inject(MAT_DIALOG_DATA) private parametre: DialogParametre,
        private dialogRef: MatDialogRef<ForfalteOppgaverDokumentValgDialogKomponent, void>,
        private avhengigheter: ForfalteOppgaverDokumentValgDialogAvhengigheter,
        private meldingTilBruker: MeldingTilBrukerNg2,
        private enumOversettelser: EnumOversettelser
    ) {}

    ngOnInit() {
        debug('ngOnInit', this.parametre.gruppeId);
        const alleEnumVerdier = Object.values(OppgaveForelderType).filter((v): v is OppgaveForelderType => typeof v === 'number');
        this.deler.set(alleEnumVerdier.map(del => ({
            enumVerdi: del,
            navn: this.enumOversettelser.oversettDokumentForelder(del),
            kontroll: new FormControl<boolean>(true, {nonNullable: true}),
            initiellVerdi: true
        })));

        this.avhengigheter.hentGruppe(this.parametre.gruppeId).then(gruppe => {
            const utelatte = gruppe.oppsett.forfalteOppgaverDokumentValg.utelatteForelderTyper;
            this.deler().forEach(del => {
                const ikkeUtelatt = !utelatte.includes(del.enumVerdi);
                del.kontroll.setValue(ikkeUtelatt);
                del.initiellVerdi = ikkeUtelatt;
            });
        });
    }

    avbryt() {
        this.dialogRef.close();
    }

    async lagre() {
        if (this.ingenDelerValgt) return;

        const harEndringer = this.deler().some(d => d.kontroll.value !== d.initiellVerdi);
        if (!harEndringer) {
            this.dialogRef.close();
            return;
        }

        const utelatteForelderTyper = this.deler().filter(d => !d.kontroll.value).map(d => d.enumVerdi);
        await this.lagrer.utfør(() => this.avhengigheter.oppdaterValg(this.parametre.gruppeId, {utelatteForelderTyper}));
        this.meldingTilBruker.infomelding(i18n('Oppgavedokument-oppsett er lagret'));
        this.dialogRef.close();
    }
}

export interface DialogParametre {
    gruppeId: GruppeId;
}

interface DelModell {
    enumVerdi: OppgaveForelderType;
    navn: string;
    kontroll: FormControl<boolean>;
    initiellVerdi: boolean;
}

@Injectable()
export class ForfalteOppgaverDokumentValgDialogAvhengigheter {

    constructor(private gruppeDatatjeneste: GruppeDatatjeneste) {}

    async hentGruppe(gruppeId: GruppeId): Promise<GruppeModellDML> {
        return firstValueFrom(this.gruppeDatatjeneste.hent(gruppeId));
    }

    async oppdaterValg(gruppeId: GruppeId, spesifikasjon: ForfalteOppgaverDokumentValgSpesifikasjon): Promise<void> {
        await this.gruppeDatatjeneste.oppdaterForfalteOppgaverDokumentValg(gruppeId, spesifikasjon);
    }
}
